import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
} from "@heroui/react"
import classes from "../../styles/NewTeacher.module.scss"
import { useEffect, useState } from "react"
import { getTeacherByUID } from "../../utils/requests/teachers"
import { useNavigate, useParams } from "react-router-dom"
import {
  attachInvoicesAccount,
  CompanyType,
  registerPartner,
} from "../../utils/requests/invoices"

const companyTypeOptions: { value: CompanyType; label: string }[] = [
  { value: "PATOOR", label: "עוסק פטור" },
  { value: "MURSHE", label: "עוסק מורשה" },
  { value: "COMPANY", label: "חברה בע״מ" },
]

const AttachInvoices = () => {
  const [loading, setLoading] = useState(false)
  const [teacher, setTeacher] = useState<TeacherDetails>()
  const [error, setError] = useState("")

  // Required user fields
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")

  // Required company fields
  const [companyId, setCompanyId] = useState("")
  const [companyName, setCompanyName] = useState("")
  const [companyType, setCompanyType] = useState<CompanyType>("PATOOR")

  // Optional fields
  const [companyEmail, setCompanyEmail] = useState("")
  const [companyTaxAmount, setCompanyTaxAmount] = useState("18")

  const navigate = useNavigate()
  const { id } = useParams()

  const fetchTeacherDetails = async () => {
    setLoading(true)
    const res = await getTeacherByUID(Number(id))
    setTeacher(res.teacher)

    // Pre-fill fields if available
    if (res.teacher) {
      setFirstName(res.teacher.firstName || "")
      setLastName(res.teacher.lastName || "")
      setCompanyId(res.teacher.idNumber || "")
      setCompanyName([res.teacher.firstName, res.teacher.lastName].filter(Boolean).join(" "))
    }
    if (res.user) {
      setPhoneNumber(res.user.phoneNumber || "")
      setEmail(res.user.email || "")
      setCompanyEmail(res.user.email || "")
    }

    setLoading(false)
  }

  useEffect(() => {
    fetchTeacherDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedCompanyType = companyTypeOptions.find(
    (opt) => opt.value === companyType
  )

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    // Step 1: Register the partner account
    const registerRes = await registerPartner({
      email,
      firstName,
      lastName,
      phoneNumber,
      companyId: String(companyId),
      companyName,
      companyType,
      ...(companyEmail && { companyEmail }),
      ...(companyTaxAmount && { companyTaxAmount: Number(companyTaxAmount) }),
    })

    if (!registerRes.success || !registerRes.apiKey) {
      setLoading(false)
      if (registerRes.error?.code === "EMAIL_EXISTS_ERROR") {
        setError("כתובת האימייל כבר קיימת במערכת")
      } else if (registerRes.error?.code === "COMPANY_EXISTS_ERROR") {
        setError("החברה כבר קיימת במערכת")
      } else {
        setError("שגיאה ביצירת החשבון")
      }
      return
    }

    // Step 2: Attach the invoice account to the teacher
    const attachRes = await attachInvoicesAccount(
      registerRes.apiKey,
      Number(teacher?.uid)
    )

    setLoading(false)

    if (!attachRes.success) {
      setError("שגיאה בחיבור החשבון למורה")
      return
    }

    navigate(-1)
  }

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <form onSubmit={onSubmit}>
          <CardHeader>יצירת חשבון חשבוניות</CardHeader>
          <Divider />
          <CardBody>
            <div className={classes.form}>
              <h4 style={{ margin: "8px 0" }}>פרטי משתמש</h4>

              <Input
                type="email"
                label="אימייל"
                disabled={loading}
                required
                value={email}
                onInput={(e) => setEmail(e.currentTarget.value)}
              />
              <Input
                type="text"
                label="שם פרטי"
                disabled={loading}
                required
                value={firstName}
                onInput={(e) => setFirstName(e.currentTarget.value)}
              />
              <Input
                type="text"
                label="שם משפחה"
                disabled={loading}
                required
                value={lastName}
                onInput={(e) => setLastName(e.currentTarget.value)}
              />
              <Input
                type="tel"
                label="מספר טלפון"
                disabled={loading}
                required
                value={phoneNumber}
                onInput={(e) => setPhoneNumber(e.currentTarget.value)}
              />

              <Divider style={{ margin: "16px 0" }} />
              <h4 style={{ margin: "8px 0" }}>פרטי עסק</h4>

              <Input
                type="text"
                label="מספר ח.פ / עוסק"
                disabled={loading}
                required
                value={companyId}
                onInput={(e) => setCompanyId(e.currentTarget.value)}
              />
              <Input
                type="text"
                label="שם העסק"
                disabled={loading}
                required
                value={companyName}
                onInput={(e) => setCompanyName(e.currentTarget.value)}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>סוג עסק</span>
                <Dropdown placement="bottom-end">
                  <DropdownTrigger>
                    <Chip color="default" variant="flat" size="lg">
                      {selectedCompanyType?.label || "בחר סוג עסק"}
                    </Chip>
                  </DropdownTrigger>
                  <DropdownMenu variant="flat">
                    {companyTypeOptions.map((option) => (
                      <DropdownItem
                        key={option.value}
                        onPress={() => setCompanyType(option.value)}
                      >
                        {option.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </div>

              <Divider style={{ margin: "16px 0" }} />
              <h4 style={{ margin: "8px 0" }}>פרטים נוספים (אופציונלי)</h4>

              <Input
                type="email"
                label="אימייל עסקי"
                disabled={loading}
                value={companyEmail}
                onInput={(e) => setCompanyEmail(e.currentTarget.value)}
              />
              <Input
                type="number"
                label="אחוז מע״מ (ברירת מחדל: 18%)"
                disabled={loading}
                value={companyTaxAmount}
                onInput={(e) => setCompanyTaxAmount(e.currentTarget.value)}
                step="1"
              />

              {error && (
                <p style={{ color: "red", textAlign: "center" }}>{error}</p>
              )}
            </div>
          </CardBody>
          <CardFooter>
            <Button type="submit" fullWidth disabled={loading} color="primary">
              יצירת חשבון וחיבור
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}

export default AttachInvoices
